import { AddOutlined, DeleteOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

type ContactType = 'phone' | 'email';

interface ContactEntry {
  type: ContactType;
  value: string;
}

export interface AddClientSubmitData {
  firstName: string;
  lastName: string;
  contacts: ContactEntry[];
}

interface AddClientDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddClientSubmitData) => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialContacts?: ContactEntry[];
  loading?: boolean;
}

const EMPTY_CONTACT: ContactEntry = { type: 'phone', value: '' };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidContact = (c: ContactEntry) => {
  if (!c.value.trim()) return false;
  if (c.type === 'email' && !EMAIL_REGEX.test(c.value.trim())) return false;
  return true;
};

const AddClientDialog: React.FC<AddClientDialogProps> = ({
  open, onClose, onSubmit,
  initialFirstName = '', initialLastName = '', initialContacts, loading = false,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [contacts, setContacts] = useState<ContactEntry[]>(initialContacts || [{ ...EMPTY_CONTACT }]);

  useEffect(() => {
    if (open) {
      setFirstName(initialFirstName);
      setLastName(initialLastName);
      setContacts(initialContacts?.length ? initialContacts : [{ ...EMPTY_CONTACT }]);
    }
  }, [open, initialFirstName, initialLastName, initialContacts]);

  const updateContact = (index: number, field: keyof ContactEntry, val: string) => {
    setContacts((prev) => prev.map((c, i) => i === index ? { ...c, [field]: val } : c));
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { ...EMPTY_CONTACT }]);
  };

  const hasValidContact = contacts.some(isValidContact);
  const hasInvalidEmail = contacts.some((c) => c.type === 'email' && c.value.trim() && !EMAIL_REGEX.test(c.value.trim()));
  const canSubmit = firstName.trim() && hasValidContact && !hasInvalidEmail;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      contacts: contacts.filter((c) => c.value.trim()),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>إضافة عميل جديد</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="الاسم الأول"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="اسم العائلة"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />

          {contacts.map((contact, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                select
                value={contact.type}
                onChange={(e) => updateContact(idx, 'type', e.target.value)}
                sx={{ minWidth: 110 }}
                size="small"
              >
                <MenuItem value="phone">هاتف</MenuItem>
                <MenuItem value="email">بريد</MenuItem>
              </TextField>
              <TextField
                label={contact.type === 'phone' ? 'رقم الهاتف' : 'البريد الإلكتروني'}
                value={contact.value}
                onChange={(e) => updateContact(idx, 'value', e.target.value)}
                fullWidth
                size="small"
                dir={contact.type === 'phone' ? 'ltr' : undefined}
                type={contact.type === 'email' ? 'email' : 'tel'}
                error={contact.type === 'email' && !!contact.value.trim() && !EMAIL_REGEX.test(contact.value.trim())}
                helperText={contact.type === 'email' && !!contact.value.trim() && !EMAIL_REGEX.test(contact.value.trim()) ? 'صيغة البريد غير صحيحة' : undefined}
              />
              {contacts.length > 1 && (
                <Tooltip title="حذف">
                  <IconButton size="small" color="error" onClick={() => removeContact(idx)} sx={{ mt: 0.5 }}>
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}

          <Button
            size="small"
            startIcon={<AddOutlined />}
            onClick={addContact}
            sx={{ alignSelf: 'flex-start' }}
          >
            إضافة وسيلة تواصل
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>إلغاء</Button>
          <Button type="submit" variant="contained" disabled={loading || !canSubmit}>
            {loading ? 'جارٍ الحفظ...' : 'حفظ'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddClientDialog;
